import { Box, Button, Heading, Input, Text, Textarea } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ActionButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyState } from '../../../components/EmptyState'
import { FormField } from '../../../components/FormField'
import { LoadingBlock } from '../../../components/Loading'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { nowSaoPauloDateTimeLocal, useAppToast } from '../../../helpers'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { JobPositionService } from '../../../services/JobPosition/JobPositionService'
import {
  IFullStep,
  IPersonnelMovementRequest,
  IPersonnelMovementRequestsAnswer,
  IQuestion
} from '../../../services/PersonnelMovementRequests/interfaces'
import PersonnelMovementRequestsService from '../../../services/PersonnelMovementRequests/PersonnelMovementRequestsService'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { PersonnelMovementBaseForm } from './PersonnelMovementBaseForm'
import { PersonnelMovementDescriptiveStep } from './PersonnelMovementDescriptiveStep'

const BASE_STEP = 1
// Same id as the API's PersonnelMovementRequestController::DESCRIPTIVE_STEP.
const DESCRIPTIVE_STEP = 12
const JOB_ROLE_QUESTION_TYPE = 'job_position'
const EMPLOYEE_QUESTION_TYPE = 'employee'
// Filled by the API from the collaborator picked on the same step, so it is never asked.
const EMPLOYEE_SECTOR_QUESTION_TYPE = 'employee_sector'
const NO_EMPLOYEES = 'Nenhum colaborador ativo neste setor. Fale com o RH para cadastrá-lo.'
const LIST_FAILED = 'Não foi possível carregar a lista. Volte uma etapa e avance de novo para tentar outra vez.'

type ApiError = AxiosError<{ errors?: Record<string, string[]> }>

/**
 * A list from the MP's sector, read once a step asks for it and again if the sector changes
 * (going back to the first step). `null` while loading; after a failure, empty and read again
 * the next time a step asks for it.
 */
function useSectorList<T>(
  isNeeded: boolean,
  sectorId: number | undefined,
  load: (sectorId: number) => Promise<T[]>,
  errorTitle: string
) {
  const toast = useAppToast()
  const [options, setOptions] = useState<T[] | null>(null)
  const [loadedSector, setLoadedSector] = useState<number | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!isNeeded || sectorId === undefined || loadedSector === sectorId) return
    setOptions(null)
    setFailed(false)
    setLoadedSector(sectorId)
    load(sectorId)
      .then(setOptions)
      .catch((error) => {
        setOptions([])
        setFailed(true)
        setLoadedSector(null)
        toast.error(errorTitle, error)
      })
  }, [isNeeded, sectorId])

  return { options, failed }
}

export default function PersonnelMovementRequestsStepPage() {
  const navigate = useNavigate()
  const toast = useAppToast()
  const { id } = useParams()
  const requestId = Number(id)
  const service = useMemo(() => new PersonnelMovementRequestsService(), [])
  const [request, setRequest] = useState<IPersonnelMovementRequest | null>(null)
  const [fullStep, setFullStep] = useState<IFullStep | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [answers, setAnswers] = useState<IPersonnelMovementRequestsAnswer>({})
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [saving, setSaving] = useState(false)
  const [isBackOpen, setIsBackOpen] = useState(false)
  const [sectors, setSectors] = useState<ISector[]>([])

  /** Reads the request again and the questions of the step it is on. */
  const load = async () => {
    setLoadFailed(false)
    try {
      const { data } = await service.getPersonnelMovementRequest(requestId)
      if (data.step === 0) {
        navigate('/personnel-movement/complete')
        return
      }
      setRequest(data)
      setErrors({})
      if (data.step === BASE_STEP || data.step === DESCRIPTIVE_STEP) {
        setFullStep(null)
        return
      }
      const stepResponse = await service.getStep(data.step)
      setFullStep(stepResponse.data)
    } catch (error) {
      setLoadFailed(true)
      toast.error('Erro ao carregar a solicitação', error)
    }
  }

  useEffect(() => {
    load()
    new SectorService()
      .listForSelect()
      .then(setSectors)
      .catch((error) => toast.error('Erro ao carregar os setores', error))
  }, [])

  const questions = (fullStep?.questions ?? []).filter((question) => question.type !== EMPLOYEE_SECTOR_QUESTION_TYPE)
  const asks = (type: string) => questions.some((question) => question.type === type)

  const { options: roleOptions, failed: rolesFailed } = useSectorList(
    asks(JOB_ROLE_QUESTION_TYPE),
    request?.sector,
    (sectorId) => new JobPositionService().listForForms(sectorId),
    'Erro ao carregar os cargos'
  )
  const { options: employeeOptions, failed: employeesFailed } = useSectorList(
    asks(EMPLOYEE_QUESTION_TYPE),
    request?.sector,
    (sectorId) => new EmployeeService().listForForms(sectorId),
    'Erro ao carregar os colaboradores'
  )

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!fullStep) return

    if (questions.some((question) => !answers[question.id])) {
      toast.invalidForm()
      return
    }

    // Only this step's answers: the earlier ones are already saved.
    const payload = Object.fromEntries(questions.map((question) => [question.id, answers[question.id]]))

    setSaving(true)
    setErrors({})
    try {
      const response = await service.update(requestId, payload)
      setAnswers({})
      if (response.data.next_step === 0) {
        navigate('/personnel-movement/complete')
        return
      }
      await load()
    } catch (error) {
      const apiErrors = (error as ApiError).response?.data?.errors
      if (apiErrors && Object.keys(apiErrors).length > 0) {
        setErrors(apiErrors)
        // Errors of no question on this step go in the description, since no field shows them.
        const fields = questions.map((question) => String(question.id))
        const unmapped = Object.entries(apiErrors).filter(([key]) => !fields.includes(key))
        toast.invalidForm(unmapped.flatMap(([, messages]) => messages).join(' ') || undefined)
        return
      }
      toast.error('Erro ao salvar as respostas', error)
    } finally {
      setSaving(false)
    }
  }

  const handlePrevious = async () => {
    try {
      const { data } = await service.previousStep(requestId)
      await load()
      // One object with every answer of the step we land on.
      setAnswers(
        Object.fromEntries(
          data.actual_step_answer.map((answer: { question_id: number; value: string }) => [
            answer.question_id,
            answer.value
          ])
        )
      )
    } catch (error) {
      toast.error('Erro ao voltar para a etapa anterior', error)
    } finally {
      setIsBackOpen(false)
    }
  }

  const submitDescriptive = async (descriptiveOk: boolean, changes?: string) => {
    try {
      await service.submitDescriptive(requestId, { descriptive_ok: descriptiveOk, descriptive_changes: changes })
      navigate('/personnel-movement/complete')
    } catch (error) {
      toast.error('Erro ao enviar a solicitação', error)
    }
  }

  const setAnswer = (questionId: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
    setErrors((prev) => ({ ...prev, [questionId]: [] }))
  }

  /** The API's error, a list that failed to load, or the notice that the sector has nobody to pick. */
  const fieldError = (question: IQuestion) => {
    if (errors[question.id]?.length) return errors[question.id]
    if (question.type === JOB_ROLE_QUESTION_TYPE && rolesFailed) return [LIST_FAILED]
    if (question.type === EMPLOYEE_QUESTION_TYPE && employeesFailed) return [LIST_FAILED]
    if (question.type === EMPLOYEE_QUESTION_TYPE && employeeOptions?.length === 0) return [NO_EMPLOYEES]
    return undefined
  }

  const renderContent = () => {
    if (loadFailed) {
      return (
        <EmptyState
          tone="error"
          icon="lni lni-warning"
          title="Não foi possível carregar a solicitação"
          action={<ActionButton action="refresh" label="Tentar novamente" showLabel onClick={load} />}
        />
      )
    }

    if (!request) return <LoadingBlock />

    if (request.step === BASE_STEP) {
      return (
        <PersonnelMovementBaseForm
          initial={{ name: request.name, email: request.email, sector_id: request.sector, type: request.type }}
          submitLabel="Continuar"
          onSubmit={async (payload) => {
            await service.updateBase(requestId, payload)
            setAnswers({})
            await load()
          }}
        />
      )
    }

    if (request.step === DESCRIPTIVE_STEP) {
      return request.job_position ? (
        <PersonnelMovementDescriptiveStep
          jobPosition={request.job_position}
          onKeep={() => submitDescriptive(true)}
          onAskChanges={(changes) => submitDescriptive(false, changes)}
          onBack={() => setIsBackOpen(true)}
        />
      ) : (
        <Text>Não encontramos o cargo escolhido. Volte para a etapa anterior e escolha o cargo de novo.</Text>
      )
    }

    if (!fullStep) return <LoadingBlock />

    return (
      <>
        <Heading as="h2" mb="4">
          {fullStep.step.title}
        </Heading>

        <form onSubmit={handleSubmit}>
          {questions.map((question) => (
            <FormField key={question.id} isRequired mb="4" label={question.label} error={fieldError(question)}>
              {question.type === 'text' && (
                <Input
                  value={answers[question.id] || ''}
                  onChange={(event) => setAnswer(question.id, event.target.value)}
                />
              )}
              {question.type === JOB_ROLE_QUESTION_TYPE && (
                <SearchableSelect<number>
                  options={(roleOptions ?? []).map((role) => ({ value: role.id, label: role.name }))}
                  value={answers[question.id] ? Number(answers[question.id]) : ''}
                  onChange={(roleId) => setAnswer(question.id, roleId === '' ? '' : String(roleId))}
                  placeholder="Selecione o cargo"
                  entity="cargo"
                  emptyMessage={rolesFailed ? LIST_FAILED : 'Nenhum cargo cadastrado para este setor. Fale com o RH.'}
                  isLoading={roleOptions === null}
                />
              )}
              {question.type === EMPLOYEE_QUESTION_TYPE && (
                <SearchableSelect<number>
                  options={(employeeOptions ?? []).map((employee) => ({
                    value: employee.id,
                    label: employee.position ? `${employee.name} · ${employee.position}` : employee.name
                  }))}
                  value={answers[question.id] ? Number(answers[question.id]) : ''}
                  onChange={(employeeId) => setAnswer(question.id, employeeId === '' ? '' : String(employeeId))}
                  placeholder="Selecione o colaborador"
                  entity="colaborador"
                  emptyMessage={employeesFailed ? LIST_FAILED : NO_EMPLOYEES}
                  isLoading={employeeOptions === null}
                />
              )}
              {question.type === 'select' && (
                <FormSelect
                  placeholder="Selecione uma opção"
                  value={answers[question.id] || ''}
                  onChange={(value) => setAnswer(question.id, value)}
                  options={
                    Array.isArray(question.options)
                      ? question.options.map((option) => ({ value: option, label: option }))
                      : sectors.map((sector) => ({ value: sector.name, label: sector.name }))
                  }
                />
              )}
              {question.type === 'textarea' && (
                <Textarea
                  value={answers[question.id] || ''}
                  onChange={(event) => setAnswer(question.id, event.target.value)}
                  height="180px"
                />
              )}
              {question.type === 'date' && (
                <Input
                  type="date"
                  value={answers[question.id] || ''}
                  onChange={(event) => setAnswer(question.id, event.target.value)}
                  min={nowSaoPauloDateTimeLocal().slice(0, 10)}
                />
              )}
            </FormField>
          ))}
          <Box display="flex" justifyContent="space-between" gap="3">
            <Button colorScheme="magenta" variant="outline" isDisabled={saving} onClick={() => setIsBackOpen(true)}>
              Voltar
            </Button>
            <Button colorScheme="magenta" isLoading={saving} type="submit">
              Próximo
            </Button>
          </Box>
        </form>
      </>
    )
  }

  return (
    <>
      <PublicFormLayout title="Movimentação de Pessoal">{renderContent()}</PublicFormLayout>

      <ConfirmDialog
        isOpen={isBackOpen}
        onClose={() => setIsBackOpen(false)}
        onConfirm={handlePrevious}
        action="save"
        title="Voltar para etapa anterior?"
        description="Ao voltar para a etapa anterior, você perderá as respostas já salvas."
        confirmLabel="Voltar"
      />
    </>
  )
}
